import { useSyncExternalStore } from 'react';
import type { NewsItem } from '../protocol/types';

type Listener = () => void;

const EMPTY_ITEMS: NewsItem[] = Object.freeze([]) as unknown as NewsItem[];

class NewsStore {
  private items: NewsItem[] = EMPTY_ITEMS;
  private itemById = new Map<string, NewsItem>();
  private listeners = new Set<Listener>();
  private itemListeners = new Map<string, Set<Listener>>();

  setSnapshot(items: NewsItem[]): void {
    const merged = new Map<string, NewsItem>();
    [...items, ...this.items].forEach((item) => merged.set(item.id, freezeNews(item)));
    this.items = Object.freeze([...merged.values()].sort((a, b) => b.time - a.time).slice(0, 500)) as NewsItem[];
    this.rebuildIndex();
    this.emit();
    items.forEach((item) => this.emitItem(item.id));
  }

  push(item: NewsItem): void {
    if (this.itemById.has(item.id)) return;
    const next = freezeNews(item);
    this.items = Object.freeze([next, ...this.items].slice(0, 500)) as NewsItem[];
    this.itemById.set(next.id, next);
    this.emit();
    this.emitItem(next.id);
  }

  markRead(id: string, read: boolean): void {
    const existing = this.itemById.get(id);
    if (!existing || existing.read === read) return;
    const updated = freezeNews({ ...existing, read });
    this.items = Object.freeze(this.items.map((item) => item.id === id ? updated : item)) as NewsItem[];
    this.itemById.set(id, updated);
    this.emit();
    this.emitItem(id);
  }

  getItems = (): NewsItem[] => this.items;

  getItem = (id: string): NewsItem | undefined => this.itemById.get(id);

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  subscribeItem = (id: string, listener: Listener): (() => void) => {
    let listeners = this.itemListeners.get(id);
    if (!listeners) {
      listeners = new Set();
      this.itemListeners.set(id, listeners);
    }
    listeners.add(listener);
    return () => {
      listeners?.delete(listener);
      if (listeners && listeners.size === 0) this.itemListeners.delete(id);
    };
  };

  private rebuildIndex(): void {
    this.itemById.clear();
    this.items.forEach((item) => this.itemById.set(item.id, item));
  }

  private emit(): void {
    [...this.listeners].forEach((listener) => listener());
  }

  private emitItem(id: string): void {
    this.itemListeners.get(id)?.forEach((listener) => listener());
  }
}

function freezeNews(item: NewsItem): NewsItem {
  return Object.freeze({ ...item, symbols: Object.freeze([...item.symbols]) as unknown as string[] });
}

export const newsStore = new NewsStore();

export function useNewsItems(): NewsItem[] {
  return useSyncExternalStore(newsStore.subscribe, newsStore.getItems, newsStore.getItems);
}

export function useNewsItem(id: string): NewsItem | undefined {
  return useSyncExternalStore(
    (listener) => newsStore.subscribeItem(id, listener),
    () => newsStore.getItem(id),
    () => newsStore.getItem(id)
  );
}
